import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { supportCaseAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import { CASE_TYPES, CASE_STATUSES, statusLabel, refLabel } from '../constants/supportCases';
import { formatCaseDate } from '../utils/formatCaseDate';
import PageHeader from '../components/PageHeader';
import SupportCaseTypeBadge from '../components/SupportCaseTypeBadge';
import DeleteIconButton from '../components/DeleteIconButton';
import { QuestionIcon, BugIcon, LightbulbIcon } from '../components/icons';
import './SupportCases.css';

const PAGE_SIZE = 25;

/**
 * Support Cases list. Shows the caller's own cases; users with
 * support_cases.view_all see all cases for the tenant (and a "Reported by" column).
 */
function SupportCases() {
  const navigate = useNavigate();
  const { tenantPath } = useTenantFromUrl();
  const [cases, setCases] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const canViewAll = hasPermission('support_cases.view_all');
  const canDelete = hasPermission('support_cases.delete');

  const loadCases = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { page: 1, pageSize: PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;
      const res = await supportCaseAPI.list(params);
      setCases(res.data?.data ?? []);
      setTotal(res.data?.total ?? 0);
    } catch (err) {
      console.error('Error loading support cases:', err);
      setCases([]);
      setTotal(0);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCases();
  }, [statusFilter, typeFilter]);

  const handleDelete = async (id, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Delete this case? This permanently removes the case, its comments, and attachments.')) return;
    try {
      await supportCaseAPI.deleteCase(id);
      await loadCases();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete case');
    }
  };

  const showClosedOn = statusFilter === 'closed';
  const colCount = (canViewAll ? 6 : 5) + (canDelete ? 1 : 0) + (showClosedOn ? 1 : 0);

  return (
    <div className="support-cases-list-page">
      <PageHeader title={canViewAll ? 'Support Cases' : 'Help & Support'}>
        {hasPermission('support_cases.create') && (
          <Link to={tenantPath('/lists/support-cases/new')} className="btn-new">
            + New case
          </Link>
        )}
      </PageHeader>

      <div className="support-cases-intro">
        {!canViewAll && (
          <p className="support-cases-intro-lead">
            Have a question, hit a bug, or have an idea to make things better? The best way to reach
            us is to <strong>open a support case</strong> — we’ll follow up right here, and you can
            track progress and message us on each one.
          </p>
        )}
          <div className="support-cases-intro-types">
            <Link to={tenantPath('/lists/support-cases/new?type=question')} className="support-cases-intro-type">
              <span className="support-cases-intro-type-icon type-question"><QuestionIcon size={22} /></span>
              <span className="support-cases-intro-type-text">
                <strong>Ask a question</strong>
                <span>Not sure how something works? We’re happy to help.</span>
              </span>
            </Link>
            <Link to={tenantPath('/lists/support-cases/new?type=bug')} className="support-cases-intro-type">
              <span className="support-cases-intro-type-icon type-bug"><BugIcon size={22} /></span>
              <span className="support-cases-intro-type-text">
                <strong>Report a bug</strong>
                <span>Something broken or acting up? A screen recording is the fastest way to show us.</span>
              </span>
            </Link>
            <Link to={tenantPath('/lists/support-cases/new?type=feature_request')} className="support-cases-intro-type">
              <span className="support-cases-intro-type-icon type-feature_request"><LightbulbIcon size={22} /></span>
              <span className="support-cases-intro-type-text">
                <strong>Suggest an improvement</strong>
                <span>Have an idea to make the app or your experience better? We’d love to hear it.</span>
              </span>
            </Link>
          </div>
      </div>

      <div className="support-cases-filters">
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} aria-label="Filter by type">
          <option value="">All types</option>
          {CASE_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Filter by status">
          <option value="">All statuses</option>
          {CASE_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      <div className="support-cases-count">
        {loading ? 'Loading...' : total === 0 ? 'No cases found' : total === 1 ? '1 case' : `${total} cases`}
      </div>

      {error && <div className="support-cases-error" role="alert">{error}</div>}

      <div className="support-cases-table">
        <table>
          <thead>
            <tr>
              <th className="col-num">Ref</th>
              <th>Subject</th>
              <th className="col-type">Type</th>
              {canViewAll && <th className="col-reporter">Reported by</th>}
              <th className="col-status">Status</th>
              <th className="col-updated">Updated</th>
              {showClosedOn && <th className="col-updated">Closed on</th>}
              {canDelete && <th className="col-actions">Delete</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={colCount} className="no-data">Loading cases...</td></tr>
            ) : cases.length === 0 ? (
              <tr><td colSpan={colCount} className="no-data">No support cases yet.</td></tr>
            ) : (
              cases.map((c) => (
                <tr
                  key={c.id}
                  className="clickable-row"
                  onClick={() => navigate(tenantPath(`/lists/support-cases/${c.id}`))}
                >
                  <td className="col-num">{refLabel(c.ref)}</td>
                  <td>{c.subject}</td>
                  <td className="col-type"><SupportCaseTypeBadge type={c.type} /></td>
                  {canViewAll && <td className="col-reporter">{c.created_by_name || c.created_by_username || '—'}</td>}
                  <td className="col-status">
                    <span className={`support-case-status status-${c.status}`}>{statusLabel(c.status)}</span>
                  </td>
                  <td className="col-updated">{formatCaseDate(c.updated_at)}</td>
                  {showClosedOn && <td className="col-updated">{formatCaseDate(c.closed_at)}</td>}
                  {canDelete && (
                    <td className="col-actions" onClick={(e) => e.stopPropagation()}>
                      <DeleteIconButton onClick={(e) => handleDelete(c.id, e)} title="Delete case" aria-label="Delete case" />
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SupportCases;
